import MoreVertIcon from '@mui/icons-material/MoreVert';
import {Menu, MenuItem, Stack, Tab, TabProps, Tooltip, Typography} from '@mui/material';
import {FC, MouseEvent, useEffect, useState} from 'react';
import {Controller} from 'react-hook-form';
import {InputField} from '../../../../../../../ui/input-field';
import {useStepsFormContext} from '../../contexts';
import {FieldGroup} from '../../../../../../../shared/types/form.types';
import {useTabSettingsStore} from '../../store/useTabSettingsStore';
import {STYLES} from './styles';
import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {getModelName} from '../../../../../../../shared/helpers/modelName';

interface Props extends TabProps {
  value: string;
  tab: FieldGroup;
  index: number;
  activeStepIndex: number;
  remove: (index: number) => void;
}

export const FieldGroupsTab: FC<Props> = ({tab, index, remove, activeStepIndex, value, ...props}) => {
  const {getSettingsById, setCanTitleEdit} = useTabSettingsStore();
  const {control, watch, setFocus, setValue} = useStepsFormContext();
  const [menuAnchor, setMenuAnchor] = useState<null | SVGSVGElement>(null);

  const isMenuOpen = Boolean(menuAnchor);
  const tabTitle = watch(`steps.${activeStepIndex}.fieldGroups.${index}.title`);
  const settings = getSettingsById(tab.id);

  const {attributes, listeners, setNodeRef, transform, transition} = useSortable({id: tab.id});

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  useEffect(() => {
    if (!settings?.canTitleEdit) return;
    setFocus(`steps.${activeStepIndex}.fieldGroups.${index}.title`);
  }, [activeStepIndex, index, setFocus, settings?.canTitleEdit]);

  useEffect(() => {
    if (tabTitle) {
      setValue(`steps.${activeStepIndex}.fieldGroups.${index}.modelName`, getModelName(tabTitle));
    }
  }, [tabTitle, activeStepIndex, index, setValue, tab.id]);

  const closeMenu = () => setMenuAnchor(null);
  const openMenu = (event: MouseEvent<SVGSVGElement>) => setMenuAnchor(event.currentTarget);

  const enableTitleEdit = () => {
    setCanTitleEdit(tab.id, true);
    closeMenu();
  };

  const deleteTab = () => {
    remove(index);
    setCanTitleEdit(tab.id, false);
    closeMenu();
  };

  const disableTitleEdit = () => setCanTitleEdit(tab.id, false);

  const renderTitle = () => {
    return settings?.canTitleEdit ? (
      <Controller
        name={`steps.${activeStepIndex}.fieldGroups.${index}.title`}
        control={control}
        render={({field}) => <InputField {...field} onBlur={disableTitleEdit} sx={STYLES.tabInput} />}
      />
    ) : (
      <Tooltip title={tabTitle}>
        <Typography sx={STYLES.tabTitle}>{tabTitle}</Typography>
      </Tooltip>
    );
  };

  return (
    <Stack ref={setNodeRef} {...attributes} style={style}>
      <Tab
        {...props}
        sx={STYLES.tab}
        value={value}
        label={
          <Stack direction="row" alignItems="center" gap={2}>
            <Stack {...attributes} {...listeners} sx={{cursor: 'grab'}}>
              <DragIndicatorIcon />
            </Stack>
            {renderTitle()}

            <MoreVertIcon sx={STYLES.moreButton} onClick={openMenu} />

            <Menu anchorEl={menuAnchor} open={isMenuOpen} onClose={closeMenu}>
              <MenuItem onClick={enableTitleEdit}>Изменить</MenuItem>
              <MenuItem onClick={deleteTab}>Удалить</MenuItem>
            </Menu>
          </Stack>
        }
      />
    </Stack>
  );
};
